import React, { useState, useEffect } from 'react';
import { Camera, CheckCircle, UploadCloud, Store, ShieldAlert, ArrowLeft } from 'lucide-react';

interface Setting {
    business_id: string;
    goal_type: 'amount' | 'visits';
    goal_target: number;
    reward_description: string;
}

export const PublicCompanyLoyalty = ({ businessId, apiUrl }: { businessId: string, apiUrl: string }) => {
    const [loading, setLoading] = useState(true);
    const [settings, setSettings] = useState<Setting | null>(null);
    const [businessName, setBusinessName] = useState('...');
    
    const [amount, setAmount] = useState('');
    const [description, setDescription] = useState('');
    const [receiptUrl, setReceiptUrl] = useState('');
    const [submitted, setSubmitted] = useState(false);
    
    // Auth Check
    const localToken = localStorage.getItem('token');

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            const res = await fetch(`${apiUrl}?action=get_company_loyalty_settings&business_id=${businessId}`);
            const data = await res.json();
            if (data.settings) {
                setSettings(data.settings);
            }
            
            // fetch business details
            const bRes = await fetch(`${apiUrl}?action=get_businesses`);
            const bData = await bRes.json();
            if (bData.businesses) {
                const b = bData.businesses.find((x: any) => String(x.id) === String(businessId));
                if (b) setBusinessName(b.name);
            }
        } catch (e) {
            console.error(e);
        }
        setLoading(false);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = {
                user_token: localToken,
                business_id: businessId,
                amount: parseFloat(amount),
                description,
                receipt_url: receiptUrl
            };
            const res = await fetch(`${apiUrl}?action=company_loyalty_receipt_submit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (data.error) {
                alert(data.error);
            } else {
                setSubmitted(true);
            }
        } catch (e) {
            alert('Erro ao enviar comprovante');
        }
        setLoading(false);
    };

    if (loading && !settings) return <div className="p-8 text-center">Carregando...</div>;

    if (!localToken) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
                <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl p-8 text-center border border-slate-100">
                    <ShieldAlert className="w-16 h-16 text-amber-500 mx-auto mb-4" />
                    <h2 className="text-xl font-black text-slate-800 mb-2">Login Necessário</h2>
                    <p className="text-sm text-slate-500 mb-8">Faça login na plataforma para registar seus pontos em {businessName}.</p>
                    <a href="/login" className="block w-full py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-bold uppercase tracking-wider text-sm transition-colors">
                        Fazer Login
                    </a>
                </div>
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
                <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl p-8 text-center border border-slate-100 animate-in zoom-in">
                    <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                    <h2 className="text-xl font-black text-slate-800 mb-2">Comprovante Enviado!</h2>
                    <p className="text-sm text-slate-500 mb-8">Sua compra foi registrada. Aguarde a aprovação do estabelecimento para receber seus pontos de fidelidade.</p>
                    <a href="/" className="block w-full py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-bold uppercase tracking-wider text-sm transition-colors">
                        Voltar ao Início
                    </a>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 p-4 pb-20">
            <div className="max-w-md mx-auto">
                <div className="flex items-center gap-4 mb-8">
                    <a href="/" className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm border text-slate-600">
                        <ArrowLeft className="w-5 h-5" />
                    </a>
                    <div>
                        <h1 className="text-xl font-black text-slate-800 flex items-center gap-2">
                            <Store className="w-5 h-5 text-purple-600" />
                            {businessName}
                        </h1>
                        <p className="text-xs font-bold text-purple-600 uppercase tracking-widest mt-1">Programa de Fidelidade</p>
                    </div>
                </div>

                {settings && (
                    <div className="bg-gradient-to-br from-purple-600 to-indigo-700 rounded-2xl p-6 text-white shadow-lg mb-8">
                        <h3 className="text-sm font-bold text-purple-200 uppercase tracking-wider mb-2">Seu Prêmio:</h3>
                        <p className="text-xl font-black leading-tight mb-4">{settings.reward_description}</p>
                        <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm border border-white/20">
                            <p className="text-sm">
                                Ganhe este prêmio ao atingir <strong>{settings.goal_target}</strong> {settings.goal_type === 'amount' ? 'reais em compras' : 'visitas/compras'}!
                            </p>
                        </div>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border p-6">
                    <h2 className="font-black text-slate-800 mb-6 flex items-center gap-2">
                        <Camera className="w-5 h-5 text-purple-500" />
                        Registrar Compra
                    </h2>
                    
                    <div className="space-y-5">
                        <div>
                            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Valor da Compra (R$)</label>
                            <input 
                                type="number" 
                                required 
                                min="0.01" 
                                step="0.01"
                                placeholder="0.00"
                                value={amount} 
                                onChange={e => setAmount(e.target.value)}
                                className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-purple-500 outline-none font-medium" 
                            />
                        </div>
                        
                        <div>
                            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Descrição (Opcional)</label>
                            <input 
                                type="text" 
                                placeholder="O que você comprou?"
                                value={description} 
                                onChange={e => setDescription(e.target.value)}
                                className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-purple-500 outline-none text-sm" 
                            />
                        </div>
                        
                        <div>
                            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Link do Comprovante (Opcional)</label>
                            <input 
                                type="url" 
                                placeholder="Link da foto do recibo..."
                                value={receiptUrl} 
                                onChange={e => setReceiptUrl(e.target.value)}
                                className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-purple-500 outline-none text-sm" 
                            />
                            <p className="text-[10px] text-slate-400 mt-2 flex items-center gap-1">
                                <UploadCloud className="w-3 h-3" />
                                Você pode fazer upload da imagem em outro lugar e colar o link aqui.
                            </p>
                        </div>

                        <button 
                            type="submit" 
                            disabled={loading || !amount}
                            className="w-full py-4 mt-4 bg-purple-600 disabled:bg-purple-300 hover:bg-purple-700 text-white rounded-xl font-black uppercase tracking-widest text-sm transition-all"
                        >
                            {loading ? 'Enviando...' : 'Enviar Comprovante'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
